import React, { useCallback, useEffect, useState } from 'react';
import useMount from 'react-use/lib/useMount';
import _ from 'lodash';
import { BlockType, OrderStrategyTypeId, SpreadType, IFeedSpread } from '@tradingblock/types';
import { useDispatcher } from '../../data/global/hooks';
import { Block } from '../../components/blocks/Block';
import { GetPositionAndOrderSymbols } from '../../data/global/dataSelectors';
import { useQuoteSymbolSubscriptionFromGlobalState, useQuoteActions } from '../../hooks/useQuoteData';
import { getBlockProps } from '../../utilities/block';
import { useGlobalStateQuoteListener } from '../../components/blocks/useBlockSubscriptions';
import { DataState } from '../../data/global/state';
import { initialPositionsAreLoading } from '../../data/global/selectors/positionSelector';
import { useFeedActions } from '../../data/feed/useFeedActions';
import { useStateSelector } from '../../data/global/dataSelectors';
import { getPositionsFromSymbol } from '../../data/global/selectors/positionSelector';
import { getSpreadInfoForPositions } from './PositionLeg';

import { usePositionSettings } from './usePositionSettings';
import { PositionHeader } from './PositionHeader';
import { PositionBlockContent } from './PositionBlockContent';

const useSubscribeToPositionSpreads = () => {
  const { setSpreads } = useQuoteActions();
  const symbolPositions = useStateSelector(getPositionsFromSymbol);

  function getEnumKeys(enumObj: any): string[] {
    return Object.keys(enumObj).filter(k => isNaN(parseInt(k)));
  }

  function getSpreadType(stragety: number) {
    const spreadTypeKeys = getEnumKeys(SpreadType);
    let sNum: number = stragety;
    return spreadTypeKeys.find(o => {
      if ([18, 11, 17, 20, 21, 25].includes(sNum) && o === SpreadType[0]) {
        //  if CreateMyOwn = 18, Box = 11, SyntheticCombo = 17,
        //  Contingent = 20, Trailing = 21, MutualFund = 25
        //  then return Custom SpreadType
        return true;
      } else {
        return o === OrderStrategyTypeId[sNum];
      }
    });
  }

  useEffect(() => {
    // Flatten all positions
    const allPositions = Object.values(symbolPositions).flatMap(s => s.positions);
    // Group by OrderId
    const grouped = _.groupBy(allPositions, p => p.OrderId);
    // Build spread definitions for groups with more than one leg
    const spreadDefs: IFeedSpread[] = Object.values(grouped)
      .filter(group => group.length > 1)
      .map(group => {
        const { name } = getSpreadInfoForPositions(group as any);
        const spreadTypeKey = getSpreadType(parseInt(group[0].StrategyTypeId.toString())) as keyof typeof SpreadType;
        const spreadType = SpreadType[spreadTypeKey as keyof typeof SpreadType]
        return {
          id: group[0].OrderId,
          name,
          type: spreadType,
          Legs: group.map(val => ({ Symbol: '.' + val.Symbol, AssetType: val.AssetType })),
        };
      });

    // Set spreads via block-level subscription
    setSpreads(spreadDefs);

    // Cleanup: clear spreads when component unmounts
    return () => {
      setSpreads([]);
    };
  }, [symbolPositions, setSpreads]);

  // TODO: Add unsubscribe for spreads when the block is unmounted
  // useEffect(() => {
  //   return (() => {
  //     const allPositions = Object.values(symbolPositions).flatMap(s => s.positions);
  //     // Group by OrderId
  //     const grouped = _.groupBy(allPositions, p => p.OrderId);
  //     // Build spread definitions for groups with more than one leg
  //     const spreadDefs = Object.values(grouped)
  //       .filter(group => group.length > 1)
  //       .map(group => {
  //         const { name } = getSpreadInfoForPositions(group);
  //         const spreadTypeKey = getSpreadType(parseInt(group[0].StrategyTypeId.toString())) as keyof typeof SpreadType;
  //         const spreadType = SpreadType[spreadTypeKey as keyof typeof SpreadType]
  //         return {
  //           id: group[0].OrderId,
  //           name,
  //           type: spreadType,
  //           Legs: group.map(val => ({ Symbol: '.' + val.Symbol, AssetType: val.AssetType, Quantity: val.OpenQuantity, Multiplier: val.OptionMultiplier > 99 ? val.OptionMultiplier / 100 : val.OptionMultiplier })),
  //         };
  //       });
  //     unsubscribeQuote(accountId, { kind: 'Spread', spreadDefinition: spreadDefs });
  //   })();
  // }, []);
};

export const PositionBlock: React.FunctionComponent<{}> = () => {
  const { dispatcher } = useDispatcher();
  useMount(() => dispatcher.data.positions.request());
  useQuoteSymbolSubscriptionFromGlobalState(GetPositionAndOrderSymbols);
  useGlobalStateQuoteListener(GetPositionAndOrderSymbols);
  useSubscribeToPositionSpreads();
  const isFetchingSelector = useCallback((state: DataState) => {
    const { loading } = initialPositionsAreLoading(state);
    return loading;
  }, []);
  const { current, definitions } = usePositionSettings();
  const { expandRows } = current;

  // Selected SubAccount States
  const [totalGain, setTotalGain] = useState<number | undefined>(undefined);
  const [dailyPnl, setDailyPnl] = useState<number | undefined>(undefined);

  const [calculatedGainValue, setCalculatedGainValue] = useState<number | undefined>(undefined);

  const setSelectedSubAccountTotalGain = async (totalGain: number | undefined) => {
    setTotalGain(totalGain);
  };

  const setSelectedSubAccountDailyPnl = async (dailyPnl: number | undefined) => {
    setDailyPnl(dailyPnl);
  };
  const accumulateTotalGainValue = async (value: number | undefined) => {
    if (!value || calculatedGainValue !== undefined) {
      return;
    }
    setCalculatedGainValue(value);
  };

  const resetTotalGainValue = () => {
    setCalculatedGainValue(undefined);
  };

  return (
    <Block
      {...getBlockProps(BlockType.Positions, undefined)}
      header={<PositionHeader totalGain={totalGain} dailyPnl={dailyPnl} expandRows={expandRows} />}
      isFetchingSelector={isFetchingSelector}
      settings={current}
      settingFields={definitions}
      onRefresh={() => {
        dispatcher.data.positions.manualRefresh();
      }}
    >
      <PositionBlockContent
        setTotalGain={setSelectedSubAccountTotalGain}
        setDailyPnl={setSelectedSubAccountDailyPnl}
        setCalculatedGainValue={accumulateTotalGainValue}
      />
    </Block>
  );
};
